import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2, X } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  fetchMembers,
  fetchPendingInvites,
  inviteUser,
  revokeInvite,
  removeMember as apiRemoveMember,
  type Role,
  type InviteIssued,
} from '@/lib/api';
import { useAuth } from '@/lib/auth-context';
import ConfirmDialog from '@/components/ConfirmDialog';

export default function UsersTab() {
  const { user, role } = useAuth();
  const isAdmin = role === 'admin';
  const qc = useQueryClient();
  const members = useQuery({
    queryKey: ['settings', 'members'],
    queryFn: fetchMembers,
    enabled: isAdmin,
  });
  const invites = useQuery({
    queryKey: ['settings', 'invites'],
    queryFn: fetchPendingInvites,
    enabled: isAdmin,
  });

  const inviteMut = useMutation({
    mutationFn: (body: { email: string; role: Role }) => inviteUser(body),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['settings', 'invites'] });
    },
  });
  const revokeMut = useMutation({
    mutationFn: (id: string) => revokeInvite(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['settings', 'invites'] }),
  });
  const removeMut = useMutation({
    mutationFn: (id: string) => apiRemoveMember(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['settings', 'members'] }),
  });

  const [showForm, setShowForm] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastIssued, setLastIssued] = useState<InviteIssued | null>(null);
  const [confirmRemove, setConfirmRemove] = useState<string | null>(null);

  if (!isAdmin) {
    return (
      <p className="text-sm text-muted-foreground">
        Only admins can manage users.
      </p>
    );
  }

  async function onInviteSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    setLastIssued(null);
    // Capture before the await — React nulls out `currentTarget`
    // once the synthetic event handler returns.
    const form = event.currentTarget;
    const fd = new FormData(form);
    try {
      const result = await inviteMut.mutateAsync({
        email: String(fd.get('email') ?? '').trim(),
        role: String(fd.get('role') ?? 'runner') as Role,
      });
      setLastIssued(result);
      form.reset();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not send invite');
    }
  }

  return (
    <div className="space-y-6">
      {/* Members */}
      <section className="space-y-3" aria-labelledby="members-heading">
        <div className="flex items-center justify-between">
          <h2 id="members-heading" className="font-display text-lg font-semibold tracking-tight">
            Members
          </h2>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={() => setShowForm((s) => !s)}
            data-testid="invite-toggle"
          >
            <Plus className="h-4 w-4 mr-1" aria-hidden /> Invite user
          </Button>
        </div>

        {showForm ? (
          <Card>
            <CardContent className="pt-5">
              <form onSubmit={onInviteSubmit} className="space-y-3" data-testid="invite-form">
                <div className="grid grid-cols-1 sm:grid-cols-[1fr_140px] gap-3">
                  <div className="space-y-1.5">
                    <Label htmlFor="email" className="font-mono text-[11px] uppercase tracking-wider">
                      Email
                    </Label>
                    <Input id="email" name="email" type="email" required className="h-11" data-testid="invite-email" />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="role" className="font-mono text-[11px] uppercase tracking-wider">
                      Role
                    </Label>
                    <select
                      id="role"
                      name="role"
                      defaultValue="runner"
                      className="h-11 w-full rounded-md border border-input bg-background px-3 text-base focus:outline-none focus:ring-2 focus:ring-ring"
                      data-testid="invite-role"
                    >
                      <option value="read">read</option>
                      <option value="runner">runner</option>
                      <option value="owner">owner</option>
                      <option value="admin">admin</option>
                    </select>
                  </div>
                </div>
                {error ? (
                  <p className="text-sm text-warning font-medium" role="alert">
                    {error}
                  </p>
                ) : null}
                <div className="flex justify-end gap-2">
                  <Button type="button" variant="ghost" onClick={() => setShowForm(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={inviteMut.isPending}>
                    {inviteMut.isPending ? 'Sending…' : 'Send invite'}
                  </Button>
                </div>
              </form>
              {lastIssued ? (
                <div className="mt-4 rounded-md border border-rule bg-muted/40 p-3 text-xs space-y-1" data-testid="invite-issued">
                  <p className="font-mono uppercase tracking-wider text-muted-foreground">
                    {lastIssued.mailDelivered ? 'Email sent' : 'Email failed — copy this link'}
                  </p>
                  <p className="font-mono break-all">{lastIssued.acceptUrl}</p>
                  {lastIssued.mailError ? (
                    <p className="text-warning">{lastIssued.mailError}</p>
                  ) : null}
                </div>
              ) : null}
            </CardContent>
          </Card>
        ) : null}

        <ul className="divide-y divide-rule rounded-md border border-rule bg-card" data-testid="members-list">
          {(members.data ?? []).map((m) => (
            <li key={m.user.id} className="flex items-center justify-between gap-3 px-4 py-3">
              <div className="min-w-0">
                <p className="font-medium truncate">{m.user.fullName ?? m.user.email}</p>
                <p className="text-xs text-muted-foreground font-mono">
                  {m.user.email} · {m.role}
                </p>
              </div>
              {m.user.id !== user?.id ? (
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  onClick={() => setConfirmRemove(m.user.id)}
                  className="text-destructive hover:bg-destructive/10 hover:text-destructive"
                  data-testid={`remove-${m.user.id}`}
                >
                  <Trash2 className="h-4 w-4" aria-hidden />
                </Button>
              ) : (
                <span className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
                  you
                </span>
              )}
            </li>
          ))}
        </ul>
      </section>

      {/* Pending invites */}
      <section className="space-y-3" aria-labelledby="invites-heading">
        <h2 id="invites-heading" className="font-display text-lg font-semibold tracking-tight">
          Pending invites
        </h2>
        {(invites.data ?? []).length === 0 ? (
          <p className="text-sm text-muted-foreground">No pending invites.</p>
        ) : (
          <ul className="divide-y divide-rule rounded-md border border-rule bg-card" data-testid="invites-list">
            {(invites.data ?? []).map((inv) => (
              <li key={inv.id} className="flex items-center justify-between gap-3 px-4 py-3">
                <div className="min-w-0">
                  <p className="font-medium truncate">{inv.email}</p>
                  <p className="text-xs text-muted-foreground font-mono">
                    {inv.role} · expires {new Date(inv.expiresAt).toLocaleDateString()}
                  </p>
                </div>
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  onClick={() => revokeMut.mutate(inv.id)}
                  className="text-muted-foreground hover:text-foreground"
                  data-testid={`revoke-${inv.id}`}
                >
                  <X className="h-4 w-4" aria-hidden />
                </Button>
              </li>
            ))}
          </ul>
        )}
      </section>

      <ConfirmDialog
        open={confirmRemove !== null}
        onOpenChange={(open) => {
          if (!open) setConfirmRemove(null);
        }}
        title="Remove this member?"
        description="They will lose access to this account immediately. They can be re-invited later."
        confirmLabel="Remove member"
        busyLabel="Removing…"
        tone="destructive"
        onConfirm={async () => {
          if (!confirmRemove) return;
          await removeMut.mutateAsync(confirmRemove);
          setConfirmRemove(null);
        }}
      />
    </div>
  );
}
